/**
 * Phase 0 — Initiation (iter84.56.7, split out of RunbookPhaseCards.jsx
 * at iter86.83). The SOLE place a promotion is started or cancelled.
 */
import { useState } from "react";
import { Play, Ban, ListChecks } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function RunbookInitiationCard({ promotion, onUpdated, onCancelled, environment }) {
    const isProd = environment === "production";

    if (!promotion) {
        return <StartPromotionForm onStarted={onUpdated} isProd={isProd} />;
    }

    return (
        <ActivePromotionHeader
            promotion={promotion}
            onUpdated={onUpdated}
            onCancelled={onCancelled}
        />
    );
}

function StartPromotionForm({ onStarted, isProd }) {
    const [title, setTitle] = useState("");
    const [busy, setBusy] = useState(false);

    if (isProd) {
        return (
            <div
                className="brutal-card p-4 border-2 border-emerald-500/40 bg-emerald-500/[0.03]"
                data-testid="phase-0-readonly-prod"
            >
                <div className="flex items-start gap-3">
                    <ListChecks className="w-5 h-5 text-emerald-400 shrink-0" />
                    <div className="flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-1">
                            Production view · read-only
                        </p>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                            New promotions are started from <strong>preview</strong>. This view
                            shows post-deploy verification status + the audit archive of finalized
                            runbooks below.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            toast.error("Title required");
            return;
        }
        setBusy(true);
        try {
            // iter84.57.6 — SHA intentionally NOT sent at start.
            // It's captured server-side by the CommitGateCard once
            // Save-to-GitHub fires (otherwise we'd be pinning a stale
            // SHA from before the operator's edits land on main).
            const r = await api.runbookStartPromotion({
                title: title.trim(),
            });
            toast.success("Promotion started — walk the gate cards below");
            onStarted?.(r);
            setTitle("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to start promotion");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            className="brutal-card p-4 border-2 border-volt/40 bg-volt/[0.03] space-y-3"
            data-testid="phase-0-start-form"
        >
            <div className="flex items-center gap-2">
                <Play className="w-4 h-4 text-volt" />
                <p className="text-[10px] font-bold uppercase tracking-widest text-volt">
                    Start a promotion
                </p>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
                Begins a new runbook attempt. Each gate card across Phases 1–3 will
                surface Approve / Waive / Deny buttons once started. Phase 2's ARM
                button unlocks after every required pre-deploy gate is satisfied.
            </p>
            <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. iter84.56.7 — Runbook UI restructure"
                data-testid="phase-0-title-input"
                className="brutal-input w-full"
            />
            {/* iter84.57.6 — SHA input removed. Captured later by the
              * CommitGateCard once the operator confirms Save-to-GitHub. */}
            <button
                type="submit"
                disabled={busy}
                data-testid="phase-0-submit-btn"
                className="brutal-btn w-full"
            >
                <Play className="w-3 h-3" /> {busy ? "Starting…" : "Start promotion"}
            </button>
        </form>
    );
}

function ActivePromotionHeader({ promotion, onCancelled }) {
    const [busy, setBusy] = useState(false);
    const isFinalized = promotion.status === "finalized";
    const isCancelled = promotion.status === "cancelled";
    const armed = !!promotion.armed_at;

    const cancel = async () => {
        if (
            !window.confirm("Cancel this promotion? You can start a fresh one anytime.")
        )
            return;
        setBusy(true);
        try {
            await api.runbookCancelPromotion(promotion.id);
            toast.success("Promotion cancelled");
            onCancelled?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        } finally {
            setBusy(false);
        }
    };

    const phase = isFinalized
        ? "finalized"
        : isCancelled
            ? "cancelled"
            : armed
                ? "armed"
                : "in_progress";
    const phaseTw = {
        finalized: {
            card: "border-2 border-emerald-500/40 bg-emerald-500/[0.03]",
            label: "FINALIZED",
            pill: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
        },
        cancelled: {
            card: "border-2 border-rose-500/40 bg-rose-500/[0.04]",
            label: "CANCELLED",
            pill: "bg-rose-500/15 text-rose-300 border-rose-500/40",
        },
        armed: {
            card: "border-2 border-volt/40 bg-volt/[0.03]",
            label: "ARMED · DEPLOY",
            pill: "bg-volt/15 text-volt border-volt/40",
        },
        in_progress: {
            card: "border-2 border-amber-500/40 bg-amber-500/[0.03]",
            label: "IN PROGRESS",
            pill: "bg-amber-500/15 text-amber-300 border-amber-500/40",
        },
    }[phase];

    return (
        <div
            data-testid="phase-0-active-header"
            data-promotion-id={promotion.id}
            data-phase={phase}
            className={`brutal-card p-3 ${phaseTw.card}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <ListChecks className="w-5 h-5 text-volt shrink-0" />
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                                Promotion
                            </p>
                            <span
                                className={`pill text-[9px] font-bold uppercase border ${phaseTw.pill}`}
                            >
                                {phaseTw.label}
                            </span>
                        </div>
                        <p
                            className="text-sm font-bold text-white truncate"
                            title={promotion.title}
                            data-testid="phase-0-title"
                        >
                            {promotion.title}
                        </p>
                        {/* iter84.57.6 — Target SHA display intentionally
                          * removed from Phase 0. Pre-iter84.57.6 we showed
                          * the SHA snapshot captured at Start, but that
                          * value pre-dated the operator's edits and was
                          * stale by the time Phase 2 needed it. SHA is now
                          * captured in the CommitGateCard once Save-to-
                          * GitHub fires; that's the SHA Phase 2 surfaces. */}
                    </div>
                </div>
                {!isFinalized && !isCancelled && (
                    <button
                        type="button"
                        onClick={cancel}
                        disabled={busy}
                        data-testid="phase-0-cancel-btn"
                        className="brutal-btn-ghost text-xs py-2 px-3"
                    >
                        <Ban className="w-3 h-3" /> Cancel promotion
                    </button>
                )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                <PhaseSummary
                    label="Pre-deploy"
                    summary={promotion.summary?.pre_deploy}
                    testId="phase-0-pre-summary"
                />
                <PhaseSummary
                    label="Post-deploy"
                    summary={promotion.summary?.post_deploy}
                    testId="phase-0-post-summary"
                />
            </div>
        </div>
    );
}

function PhaseSummary({ label, summary, testId }) {
    const s = summary || { complete: 0, waived: 0, denied: 0, pending: 0, required_pending: 0 };
    return (
        <div
            className="brutal-card-light p-2 text-zinc-300"
            data-testid={testId}
        >
            <p className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-1">
                {label}
            </p>
            <p className="font-mono text-[11px]">
                <span className="text-emerald-400">{s.complete} ✓</span>{" "}
                <span className="text-amber-400">{s.waived || 0} ⚠</span>{" "}
                <span className="text-rose-400">{s.denied || 0} ✗</span>{" "}
                <span className="text-zinc-500">{s.pending || 0} ⏳</span>
            </p>
            {s.required_pending > 0 && (
                <p className="text-[10px] text-rose-300 mt-0.5">
                    {s.required_pending} required pending
                </p>
            )}
        </div>
    );
}
